import { DateInputBlock } from '@coast/schemas';
import { useCallback, useContext } from 'react';
import { StyleSheet } from 'react-native';
import { View } from '@shared/components/View';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { formatDateWithYear, formatTime } from '@shared/util/date';
import { PickerMode } from './types';

interface Props {
  block: DateInputBlock;
  pickerMode: PickerMode;
  onOpenPicker(mode: PickerMode): void;
  onClosePicker(): void;
}

export function MBCardInputBlockDateContent({
  block,
  pickerMode,
  onOpenPicker,
  onClosePicker,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const TypoComponent = getTypoComponent(block.textStyle);
  const { values } = useContext(CardFormContext);
  const value = values[block.name];

  const openDatePicker = useCallback(() => onOpenPicker(PickerMode.Date), [onOpenPicker]);
  const openTimePicker = useCallback(() => onOpenPicker(PickerMode.Time), [onOpenPicker]);

  if (!value) {
    return <TypoComponent color={colors.foregroundInactive}>{block.placeholder}</TypoComponent>;
  }

  if (block.fieldType === 'time') {
    return <TypoComponent color={colors.foregroundActive}>{formatTime(value)}</TypoComponent>;
  }

  if (block.fieldType === 'date') {
    return (
      <TypoComponent color={colors.foregroundActive}>{formatDateWithYear(value)}</TypoComponent>
    );
  }

  return (
    <View row centerVertically>
      <MBTouchableOpacity
        style={[styles.touchable]}
        onPress={pickerMode === PickerMode.Date ? onClosePicker : openDatePicker}
      >
        <TypoComponent color={colors.foregroundActive}>{formatDateWithYear(value)}</TypoComponent>
      </MBTouchableOpacity>
      <View flex />
      <MBTouchableOpacity
        style={[styles.touchable]}
        onPress={pickerMode === PickerMode.Time ? onClosePicker : openTimePicker}
      >
        <TypoComponent color={colors.foregroundActive}>{formatTime(value)}</TypoComponent>
      </MBTouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  touchable: {
    paddingVertical: grid(1),
    paddingHorizontal: grid(1.5),
    marginLeft: -grid(1.5),
  },
  highlighted: {
    backgroundColor: Colors.v2.gray05,
    borderRadius: grid(1),
  },
});
